// import libraries
import * as $ from 'jquery';
import * as ko from 'knockout';
import 'knockout.validation'
import i18nextko from './components/util/i18nko'
import {Context, Router} from '@profiscience/knockout-contrib-router';
import 'bootstrap';
import 'knockstrap';
import {loadMarkup} from './utils/utils';
import globalState, {User} from './global-state';
import {destinationsApi, myTripsApi, publicApi, translationsApi, TripDto} from "./api/api-wrapper";
import {HEADER_INITIALIZED_EVENT} from "./constants";
import scrollToError from "./bindings/scrollToError";
import enterkey from "./bindings/enterkey";
import blur from "./bindings/blur";
import clipboard from "./bindings/clipboard";
import {postbox} from "./components/util/postbox";
import '../loader.html';
import {createConfirmModal} from "./components/elements/modal/modal";
import './components/elements/tripPictures/tripPictures';
import _merge from 'lodash-es/merge';

class ViewModelContext extends Context {
    trip: TripDto;
    ignoreRequireLogin: boolean;
}

/**
 * The root view model of the application.
 */
class AppViewModel {

    /**
     * Shows/Hides the loading indicator
     */
    public loading = globalState.loading;

    /**
     * Triggers scrollToError binding.
     */
    public hasErrors: KnockoutComputed<boolean>;

    /**
     * Flag whether the user is logged in.
     */
    public loggedIn = globalState.isLoggedIn;

    /**
     * Flag whether the app is embedded in the ReiseApp.
     */
    public isReiseApp = globalState.isReiseApp;

    /**
     * The login overlay is visible.
     */
    public loginOverlayVisible = globalState.loginOverlayVisible;

    portalJs: KnockoutObservable<string> = ko.observable('');
    portalCss: KnockoutObservable<string> = ko.observable('');
    portalTracking: KnockoutObservable<string> = ko.observable('');
    portalHeader: KnockoutObservable<string> = ko.observable('');
    portalFooter: KnockoutObservable<string> = ko.observable('');

    public showLoginOverlay() {
        console.log("showLoginOverlay");
        return App.showLoginOverlay();
    }

    constructor() {
        /**
         * Scroll to error if postbox has errors or globalState.hasErrors (to trigger the scrolling manually)
         */
        this.hasErrors = ko.pureComputed(() => {
            return postbox.errors().length > 0 || globalState.hasErrors();
        }, this).extend({rateLimit: 500});
    }
}

/**
 * The application controller.
 */
export class App {

    /**
     * The root view model instance.
     */
    public viewModel: AppViewModel;

    /**
     * Initializes the root view model.
     */
    constructor() {
        this.viewModel = new AppViewModel();

        // bind this
        this.init = this.init.bind(this);
        this.loadComponent = this.loadComponent.bind(this);
        this.loadTemplateFromQuery = this.loadTemplateFromQuery.bind(this);
        this.sessionLoaded = this.sessionLoaded.bind(this);
        this.loggedIn = this.loggedIn.bind(this);
        this.loggedOut = this.loggedOut.bind(this);
        this.requiresLogin = this.requiresLogin.bind(this);
        this.loadPortal = this.loadPortal.bind(this);
    }

    /**
     * Initializes the app:
     * - registers custom events: headerInitialized
     * - sets up the ko-component-router for navigation between pages
     * - registers loading and scrollTop router middleware
     * - registers additional binding handlers: scrollToError
     * - registers custom components: notifications, text-input
     * - loads translations: i18next
     * - initializes ko validation
     * - applies bindings to root view model
     * - finally loads header and footer resources
     */
    init() {

        // listen for header initialized events
        const headerInitialized = new Promise<void>(resolve =>
            document.addEventListener(HEADER_INITIALIZED_EVENT, () => {
                console.debug("app: header initialized");
                return resolve();
            }));

        //setup router
        Router.setConfig({
            base: '/' + config.publicPath || ''
        });

        // check, if the DRC is openend in the ReiseApp and persist this in the session
        if (location.href.indexOf('/reiseapp/') > -1 || sessionStorage.getItem('isReiseApp') === 'true') {
            sessionStorage.setItem('isReiseApp', '' + true);
            globalState.isReiseApp(true);
            globalState.reiseAppTrip(sessionStorage.getItem('reiseAppTrip') || 'neu');
        }

        Router.useRoutes({
            '/': [() =>
                headerInitialized, this.loadComponent('home/home.ts')],
            '/reiseapp/neu': [() =>
                headerInitialized, this.requiresLogin, this.setNewTrip, this.loadComponent('templates/templates.ts')],
            '/reiseapp/:tripId': [() =>
                headerInitialized, this.requiresLogin, this.setTrip,
                this.loadComponent('templates/templates.ts')],
            '/neu/vorlagen': [() => headerInitialized, () =>
                this.loadTemplateFromQuery()],
            '/neu/vorlagen/:tripId': [() =>
                headerInitialized, this.requiresLogin, this.loadComponent('groups/groups.ts')],
            '/neu/individuell': [() =>
                headerInitialized, this.requiresLogin, this.loadComponent('groups/groups.ts')],
            '/neu/individuell/:tripId': [() =>
                headerInitialized, this.requiresLogin, this.loadComponent('groups/groups.ts')],
            '/neu': [() =>
                headerInitialized, this.loadComponent('templates/templates.ts')],
            '/teilen/:shareId': [() =>
                headerInitialized, this.loadComponent('menu/menu.ts'),
                {
                    '/teilnehmen': [this.loadComponent("checklist/checklist.ts")]
                }],
            '/reise/:tripId': [() =>
                headerInitialized, this.loadComponent('menu/menu.ts'),
                {
                    '/checkliste': [this.loadComponent("checklist/checklist.ts")],
                    '/einstellungen': [this.requiresLogin, this.loadComponent("settings/settings.ts")],
                    '/teilen': [this.requiresLogin, this.loadComponent("share/edit.ts")],
                    '/versenden': [this.requiresLogin, this.loadComponent("send/send.ts")],
                    '/duplizieren': [this.loadComponent("duplicate/duplicate.ts")],
                    '/anordnen': [this.loadComponent("sort/sort.ts")],
                    '/vorlage': [this.requiresLogin, this.loadComponent('userTemplate/userTemplate.ts')]
                }]
        });
        Router.use(App.clearNotificationsMiddleware);
        Router.use(App.loadingMiddleware);
        Router.use(App.scrollTopMiddlware);
        Router.use(App.hideTripPicturesMiddleware);

        // register bindings
        ko.bindingHandlers.scrollToError = scrollToError;
        ko.bindingHandlers.enterkey = enterkey;
        ko.bindingHandlers.blur = blur;
        ko.bindingHandlers.clipboard = clipboard;

        // register components
        ko.components.register('text-input',
            require('./components/widgets/text-input').default);
        ko.components.register('notifications',
            require('./components/notifications/notifications').default);

        // register validation extenders
        ko.validation.rules['confirmed'] = {
            validator: function (val, expected) {
                return val === expected;
            },
            message: 'The field must by checked.'
        };
        ko.validation.registerExtenders();

        // localizations and validations
        return this.loadTranslations()
            .then(() => {
                // initialize validation
                ko.validation.init({
                    insertMessages: false,
                    grouping: {
                        deep: true,
                        live: true,
                        observable: true
                    },
                    messagesOnModified: true
                }, true);
                ko.validation.localize(i18nextko.t('validation', {returnObjects: true})());

                ko.applyBindings(this.viewModel);

                // include oeamtc header/footer
                this.loadPortal();
            })
            .then(() => this.checkOnline())
            .catch(err => {
                globalState.loading(false);
                postbox.addError("api.offline", {error: err}, err);

                return Promise.reject(err);
            });
    }

    /**
     * Loads and registers (if not done before) a component. Used be the router to load pages.
     * @param component the component name
     * @returns Promise<void> a Promise
     */
    loadComponent(component: string) {
        return (ctx: Context) => {
            console.debug("app: loading component " + component);

            return import("./pages/" + component)
                .then(loadedComponent => {
                    const loadedComponentDefinition =
                        <KnockoutLazyPageDefinition>loadedComponent.default;
                    console.debug("app: loaded component " + component);

                    if (!ko.components.isRegistered(loadedComponentDefinition.componentName)) {
                        ko.components.register(loadedComponentDefinition.componentName, loadedComponentDefinition);
                        console.log("app: registered component " + loadedComponentDefinition.componentName);
                    }

                    const loaderPromise = loadedComponentDefinition.loader ?
                        loadedComponentDefinition.loader(ctx) : Promise.resolve();

                    return loaderPromise.then(() => {
                        console.debug("app: setting ctx.router.component to component " +
                            loadedComponentDefinition.componentName);
                        ctx.route.component = loadedComponentDefinition.componentName;

                        return Promise.resolve();
                    });
                })
                .catch((error) => {
                    console.error("app: load component failed", error);
                    postbox.addError("load.failed", {error: error}, error);
                    globalState.loading(false);

                    return Promise.reject(error);
                });
        };
    }

    loadTemplateFromQuery() {
        const searchParams = new URLSearchParams(location.search);
        const templateId = searchParams.get('name');

        if (templateId) {
            return publicApi.getTemplates()
                .then(response => {
                    return response.data;
                })
                .then(templates => {
                    const template = templates.find(template => template.id === templateId);
                    console.debug('app: template', template);

                    if (template) {
                        return myTripsApi.createPredefinedTrip(template.id, {})
                            .then(value => value.data.tripId.toString())
                            .then(value => {
                                return Router.update(`/neu/vorlagen/${value}`, {
                                    force: false,
                                    push: false,
                                    with: {
                                        imagePath: template.picture ?
                                            `${config.templateImagePath}/${template.picture}` : config.defaultImage,
                                        templateName: template.name,
                                        ignoreRequireLogin: true,
                                        userTemplate: false,
                                        destinationNames: (searchParams.get('reiseziele') || '').split(',')
                                    }
                                });
                            })
                            .catch(err => {
                                console.error(err);

                                return this.loadComponent('templates/templates.ts')
                            });

                    } else {
                        return this.loadComponent('templates/templates.ts')
                    }
                });

        } else {
            return this.loadComponent('templates/templates.ts')
        }
    }

    setNewTrip() {
        globalState.reiseAppTrip('neu');
        sessionStorage.setItem('reiseAppTrip', 'neu');

        return Promise.resolve(true);
    }

    /**
     * Checks if a trip id has been set on /new.
     * @param ctx the router context
     */
    setTrip(ctx: ViewModelContext) {
        if (ctx.params.tripId) {
            // a trip id has been set (by the Reise App)
            return myTripsApi.getTripById(ctx.params.tripId)
                .then(result => {
                    const trip = result.data;

                    // the trip has a checklist assigned already
                    if (trip.checklistCreated) {
                        // forward to checklist directly
                        ctx.redirect(`/reise/${trip.id}/checkliste`)
                        return true;

                    } else {
                        // set trip into context for template page
                        ctx.trip = trip;
                        globalState.reiseAppTrip(ctx.params.tripId);
                        sessionStorage.setItem('reiseAppTrip', ctx.params.tripId);

                        return true;
                    }
                })
                .catch(err => {
                    // the trip could not be loaded (found), proceed to template page without trip
                    console.error('failed to load trip', err);
                    postbox.addError('global.errors.failedToLoadTrip');
                    globalState.loading(false);

                    return Promise.reject(err);
                });

        } else {
            // proceed to template selection page
            return Promise.resolve(true);
        }
    }

    /**
     * Check whether the user is logged in.
     */
    requiresLogin(ctx: ViewModelContext) {
        console.debug(ctx);
        // bypass require login if "ignoreRequireLogin" is set on routing context
        if (globalState.isLoggedIn() || ctx.ignoreRequireLogin) {
            return Promise.resolve();

        } else {
            globalState.loading(false);
            return App.loginRequiredPromise();
        }
    }

    /**
     * Page loading indicator router middleware.
     */
    static loadingMiddleware() {
        // noinspection JSUnusedGlobalSymbols
        return {
            beforeRender() {
                globalState.loading(true);
            },
            afterRender() {
                globalState.loading(false);
            }
        };
    }

    /**
     * Scroll to top after page load middleware
     */
    static scrollTopMiddlware() {
        return {
            beforeRender() {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'auto'
                });
            }
        };
    }

    /**
     * Removes error notifications on page change.
     */
    static clearNotificationsMiddleware() {
        return {
            beforeRender() {
                postbox.clearErrors();
            }
        }
    }

    static hideTripPicturesMiddleware() {
        return {
            beforeRender() {
                globalState.tripPicturesVisible(false);
            }
        }
    }

    /**
     * Checks if the user is logged in.
     * Shows a message if the user is not logged in.
     */
    static loginRequiredModal(successCallback, cancelCallback?: (() => void), backdrop?: boolean | string) {
        if (globalState.isLoggedIn()) {
            successCallback();
        } else {
            this.loginRequiredPromise(successCallback, backdrop)
                .then(() => console.debug('successfully logged in'))
                .catch(() => {
                    console.debug('login canceled');
                    if (typeof cancelCallback === 'function') {
                        cancelCallback();
                    }
                });
        }
    }

    static showLoginOverlay() {
        globalState.loginOverlayVisible(true);
        const loginOverlayPromise = new Promise(resolve => {
            $('oeamtc-login#drc-overlay-login')
                .on('login-success', () => resolve(true))
                .on('login-cancel', () => resolve(false));
        });

        return loginOverlayPromise.then(() => {
            $('oeamtc-login#drc-overlay-login')
                .off('login-success')
                .off('login-cancel');
            globalState.loginOverlayVisible(false);
        });
    }

    private static loginRequiredPromise(callback?: (() => void), backdrop?: boolean | string): Promise<void> {
        const title = i18nextko.t("login.confirmTitle");
        const text = i18nextko.t("login.confirmText");
        const confirm = i18nextko.t("global.login");
        const cancel = i18nextko.t("global.close");
        const iconCssClass = "fo-icon fo-icon-lock";

        return createConfirmModal(text, title, confirm, cancel, iconCssClass, backdrop)
            .then(() => {
                // hide login overlay
                globalState.loginOverlayVisible(true);

                // wait for login/cancel event from overlay
                const loginOverlayPromise = new Promise(resolve => {
                    $('oeamtc-login#drc-overlay-login')
                        .on('login-success', () => resolve(true))
                        .on('login-cancel', () => resolve(false));
                });

                return loginOverlayPromise.then(loginSuccess => {
                    $('oeamtc-login#drc-overlay-login')
                        .off('login-success')
                        .off('login-cancel');

                    // hide overlay
                    globalState.loginOverlayVisible(false);
                    if (loginSuccess) {
                        // perform action
                        return new Promise<void>((resolve, reject) => {
                            try {
                                if (callback && typeof callback == 'function') {
                                    callback();
                                }
                                resolve();

                            } catch (err) {
                                console.error('failed to execute login callback', err);
                                reject();
                            }
                        });

                    } else {
                        return Promise.reject();
                    }
                });
            });
    }

    loadPortal() {
        const self = this;

        Promise
            .all([
                loadMarkup('/_external/hf-assets/v1/js')
                    .then(markup => self.viewModel.portalJs(markup)),
                globalState.isReiseApp() ? Promise.resolve() : loadMarkup('/_external/hf-assets/v1/css')
                    .then(markup => self.viewModel.portalCss(markup)),
                loadMarkup('/_external/hf-assets/v1/tracking')
                    .then(markup => self.viewModel.portalTracking(markup))

            ])
            .then(() => {
                let retries = 0;
                const includeHeaderFooter = function () {
                    const cms = window['cms'];
                    if (cms) {
                        // user login
                        cms.identity.registerLoggedInCallback(self.loggedIn);

                        // user logout
                        cms.identity.registerLoggedOutCallback(self.loggedOut);

                        // existing user session is loaded after app startup
                        cms.identity.registerSessionLoadedCallback(self.sessionLoaded);

                        // load markup
                        if (globalState.isReiseApp()) {
                            self.viewModel.portalHeader('<script>window.cms.headerReady();</script>')
                        } else {
                            Promise
                                .all([
                                    loadMarkup('/_external/hf-assets/v1/header')
                                        .then(markup => self.viewModel.portalHeader(markup)),
                                    loadMarkup('/_external/hf-assets/v1/footer')
                                        .then(markup => self.viewModel.portalFooter(markup))
                                ])
                                .catch(err => console.error('failed to load header/footer markup resources', err));
                        }


                    } else if (retries < 50) { // retry if window.cms is unset
                        retries++;
                        setTimeout(includeHeaderFooter, config.windowCmsCheckerTimeout);

                    } else { // fail after x retries
                        console.error('app: failed to load header/footer');
                    }
                };

                includeHeaderFooter();
                console.info('app: successfully included header/footer js/css resources');

            })
            .catch(err => console.error('app: failed to load header/footer js/css resources', err));
    }

    /**
     * Login handler. Loads trips for current user into global state.
     * @param user the logged in user
     */
    loggedIn(user: User) {
        console.debug('app: login', user);
        globalState.user(user);
        return this.userTrips(user);
    }

    /**
     * Refreshes the globally stored trips after log out. Loads anonymous trips on app load, if user is logged out.
     */
    loggedOut() {
        console.debug('app: logout');
        const user = globalState.user();

        if (user) {
            // user was logged out
            // reset user to null
            globalState.user(null);

            // route home
            Router.update('/', {
                force: true,
                push: false,
                with: {
                    isLogout: true
                }
            }).then(() => {
                console.debug('reloaded home');
                // reset local trips after navigating home
                globalState.trips([]);
            });
        }
    }

    /**
     * Updates globalState with user data after a user session has been loaded successfully to update the views
     * accordingly.
     * @param user the looged in user
     */
    sessionLoaded(user: User) {
        console.debug('app: session loaded', user);
        // app was loaded with user logged in
        if (user.sessionId) {
            globalState.user(user);

            // load users saved trips
            return this.userTrips(user)
                .then(() => document.dispatchEvent(new CustomEvent(HEADER_INITIALIZED_EVENT))); // notify header loaded

        } else {
            // app was loaded with user logged out
            // set user explicitly to null, because session loaded event sends it as {}
            globalState.user(null);
            document.dispatchEvent(new CustomEvent(HEADER_INITIALIZED_EVENT));
        }
    }

    checkOnline(): Promise<any> {
        return destinationsApi.getContinents();
    }

    /**
     * Initialize translations.
     * @returns Promise<void> a promise when the translations have been laoded
     */
    loadTranslations() {
        const language = 'de';
        return import('../l10n/' + language + '.json')
            .then(appTranslations => {
                return translationsApi.getTranslation(language)
                    .then(responseData => {
                        return _merge({}, appTranslations, responseData.data.translations);
                    })
                    .catch(err => {
                        console.error('failed to load custom translations', err);
                        return Promise.resolve(appTranslations);
                    });
            })
            .then(translations => {
                const resourceStore = {
                    de: translations
                };
                i18nextko.init({
                    compatibilityAPI: 'v3',
                    lng: language,
                    resources: resourceStore
                });
            })
            .catch(err => {
                console.error('failed to load app translations', err);
                const resourceStore = {
                    de: {}
                };
                i18nextko.init({
                    compatibilityAPI: 'v3',
                    lng: language,
                    resources: resourceStore
                });
            });
    }

    private userTrips(user: User) {
        const anonymousTripIds = globalState.trips().map(trip => trip.id);

        return Promise
            .all(anonymousTripIds
                .map(tripId =>
                    myTripsApi.getOrCreateAnonymousTripById(String(tripId))))
            .then(() => myTripsApi.getChecklistTrips()) // reload trips for logged in user
            .then(trips => globalState.trips(trips ? trips.data : [])) // set users trips into global state
            .catch(err =>
                console.error('app: failed to copy anonymous trips to user', user, err));
    }
}
